interface PageSEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
}

export function PageSEOHead({
  title,
  description,
  keywords,
  canonicalUrl = 'https://www.koreancomic.cn/',
  ogType = 'website',
  ogImage,
}: PageSEOHeadProps) {
  const baseKeywords = '韩国漫画网, 韩国漫画网站, 韩国漫画网站大全, 免费韩漫, 韩漫在线看';

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={`${keywords}, ${baseKeywords}`} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="韩国漫画网" />
      <meta property="og:locale" content="zh_CN" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}

interface StructuredDataProps {
  data: Record<string, unknown>;
}

export function StructuredData({ data }: StructuredDataProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export function OrganizationStructuredData() {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩国漫画网',
    url: 'https://www.koreancomic.cn/',
    description: '韩国漫画网提供韩国漫画网站大全资源，海量免费韩漫在线看，支持下拉式阅读，实时追更热门漫画，日更不断更。',
    email: 'contact@koreancomic.cn',
    foundingDate: '2020',
  };
  return <StructuredData data={data} />;
}

export function WebSiteStructuredData() {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩国漫画网',
    url: 'https://www.koreancomic.cn/',
    description: '韩国漫画网站大全 | 免费韩漫在线看平台入口',
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://www.koreancomic.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
  return <StructuredData data={data} />;
}

export function WebPageStructuredData({
  title,
  description,
  url,
}: {
  title: string;
  description: string;
  url: string;
}) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
  };
  return <StructuredData data={data} />;
}

export function FAQPageStructuredData({
  faqs,
}: {
  faqs: { question: string; answer: string }[];
}) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
  return <StructuredData data={data} />;
}

export function ArticleStructuredData({
  title,
  description,
  url,
  imageUrl,
  authorName,
  datePublished,
  dateModified,
}: {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  authorName: string;
  datePublished: string;
  dateModified: string;
}) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    url: url,
    author: {
      '@type': 'Person',
      name: authorName,
    },
    datePublished: datePublished,
    dateModified: dateModified,
    ...(imageUrl && { image: imageUrl }),
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };
  return <StructuredData data={data} />;
}